import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap, shareReplay, of } from 'rxjs';
import { environment } from '../environments/environment';
import {
  OfferWithImage,
  OfferWithImageHTTPResponse,
} from '../model/offer-with-image';
import { OfferType } from '../model/offer-type';
import { User } from '@/model/user';
import { UserService } from './user.service';
import { KeycloakService } from 'keycloak-angular';
import { Feedback, FeedbackResponse } from '@/model/feedback';
import { CompanyUser } from '@/model/company';
import { Category } from '@/model/category';
import { LocationSuggestion } from '@/model/offer-with-image';
import { StructuredSearchFilterDto } from '@/model/dto/structured-search-filter.dto';

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Structured search filter moved to DTO: StructuredSearchFilterDto

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private baseUrl = `${environment.api}/offers`;
  private apiRestUrl = `${environment.api}`;
  private apiUrl = `${environment.api}/graphql`;
  private workflowUrl = `${environment.api}`;

  // Cache for offers
  private offerCache = new Map<string, OfferWithImage>();
  private offersListCache = new Map<string, OfferWithImage[]>();
  private companyDetailsCache = new Map<string, CompanyUser>();

  constructor(
    public http: HttpClient,
    private userService: UserService,
    private keycloakService: KeycloakService,
  ) {}

  // Cache management methods
  private getCachedOffer(id: string): OfferWithImage | null {
    return this.offerCache.get(id) || null;
  }

  private setCachedOffer(offer: OfferWithImage) {
    this.offerCache.set(offer.id, offer);
  }

  private getCachedCompanyDetails(offerId: string): CompanyUser | null {
    return this.companyDetailsCache.get(offerId) || null;
  }

  private setCachedCompanyDetails(offerId: string, details: CompanyUser) {
    this.companyDetailsCache.set(offerId, details);
  }

  private getOffersListCacheKey(limit: number, offset: number): string {
    return `offers_${limit}_${offset}`;
  }

  private getOffersListCacheKeyWithTag(
    tag: string,
    limit: number,
    offset: number,
  ): string {
    return `${tag}_offers_${limit}_${offset}`;
  }

  private getCachedOffersList(
    limit: number,
    offset: number,
  ): OfferWithImage[] | null {
    const cacheKey = this.getOffersListCacheKey(limit, offset);
    return this.offersListCache.get(cacheKey) || null;
  }

  private setCachedOffersList(
    offers: OfferWithImage[],
    limit: number,
    offset: number,
  ) {
    const cacheKey = this.getOffersListCacheKey(limit, offset);
    this.offersListCache.set(cacheKey, offers);
    // Also cache individual offers
    offers.forEach((offer) => this.setCachedOffer(offer));
  }

  public clearOfferCache() {
    this.offerCache.clear();
    this.offersListCache.clear();
    this.companyDetailsCache.clear();
  }

  // ===== GraphQL query builders (to avoid duplications) =====
  private readonly OFFER_GRAPHQL_FIELDS = `
    id
    name
    description
    companyId
    imageUrl
    appointmentDateTime
    videoEmbeddedLink
    status
    orte { id name plz }
    bezirke { id name }
    bundeslaender { id name }
    categories { id name }
    offerType { id offerName acceptedMimeType requiresDate icon }
  `;

  private readonly PAGINATION_GRAPHQL_FIELDS = `
    totalCount
    totalPages
    currentPage
    limit
    offset
    hasNext
    hasPrevious
  `;

  private buildOffersResponseQuery(operationName: string): string {
    return `
      query ${operationName}($limit: Int!, $offset: Int!) {
        ${operationName}(limit: $limit, offset: $offset) {
          offers { ${this.OFFER_GRAPHQL_FIELDS} }
          pagination { ${this.PAGINATION_GRAPHQL_FIELDS} }
        }
      }
    `;
  }

  private fetchOffersResponse(
    operationName: 'allApprovedOffers' | 'allOffers',
    cacheTag: string,
    limit: number,
    offset: number,
  ): Observable<OfferWithImage[]> {
    const cacheKey = this.getOffersListCacheKeyWithTag(cacheTag, limit, offset);
    const cached = this.offersListCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const query = this.buildOffersResponseQuery(operationName);
    const variables = { limit, offset };

    return this.http
      .post<{
        data: Record<
          string,
          {
            offers: OfferWithImageHTTPResponse[];
            pagination: PaginationInfo;
          }
        >;
      }>(this.apiUrl, { query, variables })
      .pipe(
        map((response) => {
          const payload = response.data[operationName] as unknown as {
            offers: OfferWithImageHTTPResponse[];
            pagination: PaginationInfo;
          };
          const offers = payload.offers.map((o) =>
            OfferWithImage.fromHTTPResponse(o),
          );
          this.offersListCache.set(cacheKey, offers);
          offers.forEach((o) => this.setCachedOffer(o));
          return offers;
        }),
        shareReplay(1),
      );
  }

  userIsAssociatedWithOffer(offer: OfferWithImage, user: User): boolean {
    const roles = this.keycloakService.getUserRoles();

    if (!user.company) {
      return false;
    }

    return (
      offer.companyId.toString() === user.company.id ||
      roles.includes('admin') === true
    );
  }

  getAllOfferTypes(): Observable<OfferType[]> {
    const query = `
      query {
        allOfferTypes {
          id
          offerName
          acceptedMimeType
          requiresDate
          icon
        }
      }
    `;

    return this.http
      .post<{
        data: {
          allOfferTypes: OfferType[];
        };
      }>(this.apiUrl, { query })
      .pipe(map((response) => response.data.allOfferTypes as OfferType[]));
  }

  getOfferTypeById(id: string): Observable<OfferType> {
    const query = `
      query offerTypeById($id: ID!) {
        offerTypeById(id: $id) {
          id
          offerName
          acceptedMimeType
          requiresDate
          icon
        }
      }
    `;

    const variables = { id };

    return this.http
      .post<{
        data: {
          offerTypeById: OfferType;
        };
      }>(this.apiUrl, { query, variables })
      .pipe(map((response) => response.data.offerTypeById as OfferType));
  }

  getSearchSuggestions(searchTerm: string): Observable<{
    offers: OfferWithImage[];
    categories: Category[];
    locations: LocationSuggestion[];
  }> {
    const query = `
    query getSearchSuggestions($searchTerm: String!) {
      getSearchSuggestions(searchTerm: $searchTerm) {
        categories {
          id
          name
        }
        ort {
          id
          name
          plz
        }
        offer {
          id
          name
          description
        }
      }
    }
  `;

    const variables = { searchTerm };

    return this.http
      .post<{
        data: {
          getSearchSuggestions: {
            categories: Category[];
            ort: LocationSuggestion[];
            offer: OfferWithImage[];
          };
        };
      }>(this.apiUrl, { query, variables })
      .pipe(
        map((response) => {
          const { categories, ort, offer } = response.data.getSearchSuggestions;
          return {
            offers: offer,
            categories: categories,
            locations: ort,
          };
        }),
      );
  }

  searchOffersByStructuredFilter(
    filter: StructuredSearchFilterDto,
  ): Observable<OfferWithImage[]> {
    const query = `
      query extendedSearch(
        $searchTerm: [String],
        $categories: [ID],
        $offerTypes: [ID],
        $limit: Int!,
        $offset: Int!,
        $postalCodes: [String]
      ) {
        extendedSearch(
          searchTerm: $searchTerm,
          categories: $categories,
          offerTypes: $offerTypes,
          limit: $limit,
          offset: $offset,
          postalCodes: $postalCodes
        ) {
          id
          name
          description
          imageUrl
          status
          appointmentDateTime
          videoEmbeddedLink
          orte {
            name
          }
          bezirke {
            name
          }
          bundeslaender {
            name
          }
          offerType {
            id
            offerName
            acceptedMimeType
            requiresDate
            icon
          }
          categories {
            id
            name
          }
        }
      }
    `;

    // Convert fulltext array to single search term and handle empty arrays
    const variables = {
      searchTerm: filter.fulltext.length > 0 ? filter.fulltext : null,
      categories: filter.categories.length > 0 ? filter.categories : null,
      offerTypes: filter.offerTypes.length > 0 ? filter.offerTypes : null,
      limit: filter.limit || 20,
      offset: filter.offset || 0,
      postalCodes: filter.postalCode || null,
    };

    return this.http
      .post<{
        data: {
          extendedSearch: OfferWithImage[];
        };
      }>(this.apiUrl, { query, variables })
      .pipe(
        map((response) => {
          return response.data.extendedSearch;
        }),
      );
  }

  getAllOffersWithImages(
    limit: number,
    offset: number,
  ): Observable<OfferWithImage[]> {
    return this.fetchOffersResponse(
      'allApprovedOffers',
      'approved',
      limit,
      offset,
    );
  }

  // New: returns all non-deleted offers across all statuses
  getAllOffersAnyStatus(
    limit: number,
    offset: number,
  ): Observable<OfferWithImage[]> {
    return this.fetchOffersResponse('allOffers', 'all', limit, offset);
  }

  // Returns all offers (any status) for the authenticated user from backend
  getMyOffers(limit = 100, offset = 0): Observable<OfferWithImage[]> {
    return this.http
      .get<OfferWithImageHTTPResponse[]>(`${this.baseUrl}/for-company`, {
        params: {
          limit: String(limit),
          offset: String(offset),
        },
      })
      .pipe(map((resp) => resp.map((o) => OfferWithImage.fromHTTPResponse(o))));
  }

  getWorkflowHistory(id: string): Observable<FeedbackResponse[]> {
    return this.http.get<FeedbackResponse[]>(`${this.baseUrl}/history/${id}`);
  }

  addOfferWithImage(formData: FormData): Observable<null> {
    this.clearOfferCache();
    return this.http.post<null>(`${this.baseUrl}/image`, formData);
  }

  getOfferById(id: string): Observable<OfferWithImage> {
    // Check cache first
    const cachedOffer = this.getCachedOffer(id);
    if (cachedOffer) {
      return of(cachedOffer);
    }

    const query = `
      query offerById($id: ID!) {
        offerById(id: $id) {
          id
          name
          description
          companyId
          imageUrl
          appointmentDateTime
          videoEmbeddedLink
          status
          orte {
            id
            name
            plz
          }
          bezirke {
            id
            name
          }
          bundeslaender {
            id
            name
          }
          categories {
            id
            name
          }
          offerType {
            id
            offerName
            acceptedMimeType
            requiresDate
            icon
          }
        }
      }
    `;

    const variables = { id };

    return this.http
      .post<{
        data: {
          offerById: OfferWithImageHTTPResponse;
        };
      }>(this.apiUrl, { query, variables })
      .pipe(
        map((response) =>
          OfferWithImage.fromHTTPResponse(response.data.offerById),
        ),
        tap((offer: OfferWithImage) => this.setCachedOffer(offer)),
        shareReplay(1),
      );
  }

  editOfferWithImage(formData: FormData): Observable<null> {
    const offerId = formData.get('id');
    this.clearOfferCache();
    return this.http.put<null>(`${this.baseUrl}/${offerId}`, formData);
  }

  deleteOffer(offerId: string): Observable<null> {
    return this.http.delete<null>(`${this.baseUrl}/${offerId}`);
  }

  uploadOffersFile(
    fileData: FormData,
    categoryIds: string[],
  ): Observable<null> {
    fileData.append('categorieIds', JSON.stringify(categoryIds));
    return this.http.post<null>(`${this.baseUrl}/file`, fileData);
  }

  submitOfferFeedback(
    offerId: string,
    feedback: Feedback,
  ): Observable<Feedback> {
    this.clearOfferCache();
    return this.http.post<Feedback>(
      `${this.apiRestUrl}/api/offers/${offerId}/feedback`,
      feedback,
    );
  }

  getCompanyDetailsByOfferId(offerId: string): Observable<CompanyUser> {
    // Check cache first
    const cachedDetails = this.getCachedCompanyDetails(offerId);
    if (cachedDetails) {
      return of(cachedDetails);
    }

    return this.http
      .get<CompanyUser>(`${this.baseUrl}/company/${offerId}`)
      .pipe(
        tap((details: CompanyUser) =>
          this.setCachedCompanyDetails(offerId, details),
        ),
        shareReplay(1),
      );
  }

  /**
   * Get pagination metadata for approved offers
   */
  private fetchOffersPagination(
    operationName: 'allApprovedOffers' | 'allOffers',
    limit: number,
    offset: number,
  ): Observable<PaginationInfo> {
    const query = this.buildOffersResponseQuery(operationName);
    const variables = { limit, offset };
    return this.http
      .post<{
        data: Record<string, { pagination: PaginationInfo }>;
      }>(this.apiUrl, { query, variables })
      .pipe(
        map(
          (response) =>
            response.data[operationName].pagination as PaginationInfo,
        ),
        shareReplay(1),
      );
  }

  getApprovedOffersPaginationMetadata(
    limit: number,
    offset: number,
  ): Observable<PaginationInfo> {
    return this.fetchOffersPagination('allApprovedOffers', limit, offset);
  }

  // New: pagination for all non-deleted offers (any status)
  getAllOffersPaginationMetadata(
    limit: number,
    offset: number,
  ): Observable<PaginationInfo> {
    return this.fetchOffersPagination('allOffers', limit, offset);
  }
}
